import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import Colors from 'styles/export/colors.module.scss';
import styles from './ExpensesChart.module.scss';
import { VictoryLabel, VictoryPie } from 'victory';
import Utils from 'common/services/Utils';
import { ExpensesDashboardDto } from 'api/dashboard/dtos/ExpensesDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import { DashboardExpenseTypeEnum } from 'api/dashboard/enums/DashboardExpenseTypeEnum';
import useElementSize from 'common/hooks/useElementSize';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import Box from 'common/components/box/Box';
import { DASHBOARD_COLORS } from 'Config';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import useWidgetFilters from '../hooks/useWidgetFilters';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import ChartTooltipPortal from '../components/ChartTooltipPortal';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';

type Props = {
	// Legacy props (direct data passing)
	data?: ExpensesDashboardDto | null;
	isLoading?: boolean;
	// New props (self-fetch mode)
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const CONTRACTS_FILTERS_CACHE_KEY = 'contracts-listing-filters';
const MAINTENANCES_FILTERS_CACHE_KEY = 'vehicle-maintenance-filters-0';
const CHARGES_FILTERS_CACHE_KEY = 'CHARGESFILTER-0';

const ExpensesChart = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [hiddenData, setHiddenData] = useState<DashboardExpenseTypeEnum[]>([]);
	const [containerRef, { width, height }] = useElementSize();
	const [fetchedData, setFetchedData] = useState<ExpensesDashboardDto | null>(null);
	const [fetchLoading, setFetchLoading] = useState(false);
	const [tooltip, setTooltip] = useState<{ name: string; value: number; color: string; clientX: number; clientY: number } | null>(null);

	const isLegacyMode = props.data !== undefined;
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('expensesChart');
	const vehicleScope = vehicleScopeCriteria(useDashboardVehicleScope());
	const isControlled = props.filters != null;
	const effectiveDateFrom = props.filters?.dateFrom ?? dateFrom;
	const effectiveDateTo = props.filters?.dateTo ?? dateTo;

	useEffect(() => {
		if (isLegacyMode) return;
		let cancelled = false;
		const fetchData = async () => {
			setFetchLoading(true);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: { dateFrom, dateTo, ...vehicleScope };
				const result = await DashboardService.getExpenses(criteria);
				if (!cancelled) {
					setFetchedData(result);
				}
			} catch {
				if (!cancelled) {
					addToast(t('common.messages.error_load_info'), { appearance: 'error' });
				}
			} finally {
				if (!cancelled) {
					setFetchLoading(false);
				}
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, isLegacyMode, dateFrom, dateTo]);

	const data = isLegacyMode ? props.data! : fetchedData;
	const isLoading = isLegacyMode ? props.isLoading : fetchLoading;

	if (!data && !isLoading) {
		return null;
	}

	if (!data?.items || data?.items.length === 0) {
		const emptyContent = isLoading ? (
			<div className={styles.centerAlign}>
				<LoadingSpinner />
			</div>
		) : (
			<WidgetNoData />
		);

		if (isLegacyMode) {
			return <>{emptyContent}</>;
		}

		return (
			<Box className={tableStyles.container} title={t('dashboard.expenses') as string}
				subTitleTooltip={t('dashboard.expenses_tooltip' as any) as string}
				renderOptions={isControlled ? undefined : <WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />}>
				{emptyContent}
			</Box>
		);
	}

	const itemsData = data?.items.filter(x => !hiddenData.find(h => h === x.type));

	const categories = itemsData.map(x => x.type);
	const colorScale = itemsData.map(x => x.isSystem ? DASHBOARD_COLORS.expenses[(x.type as DashboardExpenseTypeEnum)] : x.color!);

	const legendData = Utils.sortAlphabetically(data?.items.map(x => ({
		name: x.isSystem ? t(`dashboard.expenses_types.${x.type}` as any) : x.type,
		type: x.type,
		symbol: {
			fill: !hiddenData.find(a => a === x.type) ? (x.isSystem ? DASHBOARD_COLORS.expenses[(x.type as DashboardExpenseTypeEnum)] : x.color) : Colors.gray17,
		},
		labels: { fill: Colors.primaryDark },
		clickable: true,
	})), false, 'name');

	const total = itemsData.map(x => x.value).reduce((a, b) => a + b, 0);

	const onClickLegend = (propsData: any) => {
		const type = propsData.datum.type;

		if (hiddenData.find(x => x === type)) {
			setHiddenData(hiddenData.filter(x => x !== type));
		} else {
			setHiddenData([...hiddenData, type]);
		}
	};

	// Layout calculations — legend goes right when wide, bottom when narrow
	const isHorizontal = width > height * 1.3;
	const legendWidth = isHorizontal ? Math.min(width * 0.38, 160) : 0;
	const donutAreaWidth = isHorizontal ? width - legendWidth : width;
	const donutAreaHeight = isHorizontal ? height : Math.max(height - 30, 150);
	const donutSize = Math.min(donutAreaWidth, donutAreaHeight);
	const pieRadius = donutSize * 0.44;
	const pieInnerRadius = pieRadius * 0.7467;


	const goToExpenseList = (item: { type: string; typeId?: string | null; isSystem?: boolean }) => {
		// inactives/vehiclesInactive: false — o dashboard agrega só viaturas ativas;
		// o clique tem de levar à listagem com o mesmo critério para os totais baterem.
		if (item.type === DashboardExpenseTypeEnum.CONTRACTS) {
			// Contratos activos no período: start_date <= dateTo AND end_date >= dateFrom
			try {
				localStorage.setItem(CONTRACTS_FILTERS_CACHE_KEY, JSON.stringify({
					contractDateFrom: effectiveDateFrom,
					contractDateTo: effectiveDateTo,
					showOnlyLast: true,
					vehiclesInactive: false,
				}));
			} catch {
				// ignore
			}
			window.open('/contracts', '_blank');
			return;
		}
		if (item.type === DashboardExpenseTypeEnum.MAINTENANCES) {
			// O dashboard agrega por `vm.maintenance_date` (data de execução) — então
			// preenchemos o filtro "Data de execução" da listagem, não o da "Data prevista".
			try {
				localStorage.setItem(MAINTENANCES_FILTERS_CACHE_KEY, JSON.stringify({
					executedStartDate: effectiveDateFrom,
					executedEndDate: effectiveDateTo,
					vehiclesInactive: false,
				}));
			} catch {
				// ignore
			}
			window.open('/maintenances', '_blank');
			return;
		}
		// All other types map to the standalone charges list filtered by generic_type + the widget's date range.
		try {
			const raw = localStorage.getItem(CHARGES_FILTERS_CACHE_KEY);
			const existing = raw ? JSON.parse(raw) : {};
			const typeName = item.isSystem ? (t(`dashboard.expenses_types.${item.type}` as any) as string) : item.type;
			const next = {
				...existing,
				typeId: item.typeId ?? undefined,
				typeName,
				startDate: effectiveDateFrom,
				endDate: effectiveDateTo,
				vehiclesInactive: false,
				page: 1,
				scrollToItemId: undefined,
			};
			localStorage.setItem(CHARGES_FILTERS_CACHE_KEY, JSON.stringify(next));
		} catch {
			// ignore
		}
		window.open('/charges', '_blank');
	};

	// Pie events for tooltip + click-through
	// eslint-disable-next-line
	const pieEvents: any[] = [
		{
			target: 'data',
			eventHandlers: {
				onMouseEnter: (evt: any, targetProps: any) => {
					const datum = targetProps.datum;
					const itemData = data?.items.find(x => x.type === datum.type);
					const name = itemData?.isSystem ? t(`dashboard.expenses_types.${datum.type}` as any) : datum.type;
					const color = itemData?.isSystem ? DASHBOARD_COLORS.expenses[(datum.type as DashboardExpenseTypeEnum)] : itemData?.color || '#999';
					setTooltip({
						name: name as string,
						value: datum.value,
						color,
						clientX: evt.clientX,
						clientY: evt.clientY,
					});
					return [];
				},
				onMouseMove: (evt: any) => {
					setTooltip(prev => prev ? { ...prev, clientX: evt.clientX, clientY: evt.clientY } : null);
					return [];
				},
				onMouseLeave: () => {
					setTooltip(null);
					return [];
				},
				onClick: (_evt: any, targetProps: any) => {
					const idx = targetProps.index as number;
					const item = itemsData[idx];
					if (item) goToExpenseList(item);
					return [];
				},
			},
		},
	];

	const grandTotal = data?.items.reduce((a, b) => a + b.value, 0) ?? 0;
	const legendItems = legendData.map((item, i) => {
		// legendData is sorted alphabetically; positional lookup into data.items would misalign names with values.
		// Match by type instead.
		const sourceItem = data?.items.find(x => x.type === item.type);
		const itemValue = sourceItem?.value ?? 0;
		// 2 casas decimais para evitar que fatias pequenas (ex.: 0,3%) apareçam como "0%"
		// e garantir que a soma bate os 100%.
		const pct = grandTotal > 0 ? ((itemValue / grandTotal) * 100).toFixed(2).replace('.', ',') : '0,00';
		const hidden = !!hiddenData.find(h => h === (item.type as DashboardExpenseTypeEnum));
		return (
			<span
				key={i}
				className={`${styles.legendItem} ${hidden ? styles.legendItemHidden : ''}`}
				onClick={() => onClickLegend({ datum: { type: item.type } })}
			>
				<span className={styles.legendDot} style={{ backgroundColor: (item.symbol as any)?.fill || '#999' }} />
				<span className={styles.legendName}>{item.name}</span>
				<span className={styles.legendPct}>{pct}%</span>
			</span>
		);
	});

	const chartContent = (
		<div className={styles.chartContainer} ref={containerRef}>
			{isLoading ? <div className={styles.centerAlign}><LoadingSpinner /></div> :
				width > 0 && height > 0 && (
					<div className={isHorizontal ? styles.layoutHorizontal : styles.layoutVertical}>
						<div className={styles.donutWrapper} style={{ width: donutAreaWidth, height: donutAreaHeight }}>
							<svg width={donutAreaWidth} height={donutAreaHeight}>
								<VictoryPie
									standalone={false}
									colorScale={colorScale}
									categories={categories}
									innerRadius={pieInnerRadius}
									radius={pieRadius}
									width={donutAreaWidth}
									height={donutAreaHeight}
									data={itemsData}
									x="type"
									y="value"
									events={pieEvents}
									style={{ data: { cursor: 'pointer', stroke: '#fff', strokeWidth: 1 } }}
									labelComponent={<VictoryLabel text="" />}
								/>
							</svg>
							{(() => {
								// € colado ao número (poupa 1 char) + auto-shrink baseado no
								// comprimento do texto para nunca quebrar em duas linhas em
								// valores grandes tipo "1 226 277,54€".
								const totalText = Utils.formatCurrency(total).replace(' €', '€');
								const innerWidth = pieInnerRadius * 1.7;
								const baseFontSize = pieInnerRadius * 0.28;
								const approxCharWidth = baseFontSize * 0.55;
								const textWidth = totalText.length * approxCharWidth;
								const fitFactor = textWidth > innerWidth ? innerWidth / textWidth : 1;
								const fontSize = Math.max(baseFontSize * fitFactor, 9);
								return (
									<div className={styles.centerLabel} style={{ maxWidth: innerWidth }}>
										<div className={styles.centerLabelSub}>{t('dashboard.total' as any)}</div>
										<div className={styles.centerLabelValue} style={{ fontSize }}>{totalText}</div>
									</div>
								);
							})()}
						</div>
						<div className={isHorizontal ? styles.htmlLegendRight : styles.htmlLegend}>
							{legendItems}
						</div>
					</div>
				)}

			{tooltip && (
				<ChartTooltipPortal clientX={tooltip.clientX} clientY={tooltip.clientY} className={styles.tooltip}>
					<div className={styles.tooltipRow}>
						<span className={styles.tooltipDot} style={{ backgroundColor: tooltip.color }} />
						<span>{tooltip.name}</span>
					</div>
					<div className={styles.tooltipValue}>
						{Utils.formatCurrency(tooltip.value)}
					</div>
				</ChartTooltipPortal>
			)}
		</div>
	);

	if (isLegacyMode) {
		return chartContent;
	}

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.expenses') as string}
			subTitleTooltip={t('dashboard.expenses_tooltip' as any) as string}
			renderOptions={isControlled ? undefined : <WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />}
		>
			{chartContent}
		</Box>
	);
};

export default ExpensesChart;
